import { Grid } from '@/common/grid/Grid';
import { useEffect, useRef, useState } from 'react';

// URA is a per-unit rate, so it needs more than cents to reproduce the amounts
const RATE_PRECISION = 6;
const ura = (prefix) => ({
  colId: prefix + 'Ura',
  field: 'ura',
  displayName: 'URA',
  type: 'MONEY',
  decimalPrecision: RATE_PRECISION,
});

// The line's movements show Units × URA = Amount, as in McdInvTrendDetailGrid
const fieldDefs = [
  { field: 'invLineCode', displayName: 'Invoice Line', type: 'HYPERLINK', module: 'sc', entity: 'MCD_INV_LINE' },
  { field: 'invPeriodCode', displayName: 'Rebate Period' },
  { field: 'earnedPeriodCode', displayName: 'Earned Period' },
  { field: 'stateName', displayName: 'State' },
  { field: 'ctrtName', displayName: 'Contract' },
  { field: 'brandCode', displayName: 'Brand' },
  { field: 'ndc11Code', displayName: 'NDC11' },
  { field: 'invOrigin', displayName: 'Invoice Origin' },
  { field: 'dispCodeStr', displayName: 'Dispute Codes' },
  {
    displayName: 'Invoiced',
    children: [
      { field: 'invUnits', displayName: 'Units', type: 'NUMBER' },
      ura('inv'),
      { field: 'invAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Disputed',
    children: [
      { field: 'dispUnits', displayName: 'Units', type: 'NUMBER' },
      ura('disp'),
      { field: 'dispAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Paid',
    children: [
      { field: 'paidUnits', displayName: 'Units', type: 'NUMBER' },
      ura('paid'),
      { field: 'paidAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Due',
    children: [
      { field: 'dueUnits', displayName: 'Units', type: 'NUMBER' },
      ura('due'),
      { field: 'dueAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  // A single line can be split across multiple outcomes (e.g. partially Accepted, partially Rejected Paid), so each
  // gets its own units/amount pair rather than one "Outcome" column. Units are magnitudes while amounts carry the
  // line's sign, so these have no URA column
  {
    displayName: 'Created',
    children: [
      { field: 'createdUnits', displayName: 'Units', type: 'NUMBER' },
      { field: 'createdAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Accepted',
    children: [
      { field: 'acceptedUnits', displayName: 'Units', type: 'NUMBER' },
      { field: 'acceptedAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Rejected (Paid)',
    children: [
      { field: 'rejectedPaidUnits', displayName: 'Units', type: 'NUMBER' },
      { field: 'rejectedPaidAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Rejected (Due)',
    children: [
      { field: 'rejectedDueUnits', displayName: 'Units', type: 'NUMBER' },
      { field: 'rejectedDueAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'URA Revision',
    children: [{ field: 'uraRevisionAmt', displayName: 'Amount', type: 'MONEY' }],
  },
];

// Maps each x-axis dimension to the grid column that filtering by it corresponds to
const xAxisToField = {
  RBT_PERIOD: 'invPeriodCode',
  EARNED_PERIOD: 'earnedPeriodCode',
  NDC11_PROD: 'ndc11Code',
  BRAND: 'brandCode',
  STATE: 'stateName',
  CTRT: 'ctrtName',
};

// Maps a clicked series bucket to the grid column that is non-zero for that outcome; URA Revisions move no units
const bucketToFilterField = {
  CREATED: 'createdUnits',
  ACCEPTED: 'acceptedUnits',
  REJECTED_PAID: 'rejectedPaidUnits',
  REJECTED_DUE: 'rejectedDueUnits',
  URA_REVISION: 'uraRevisionAmt',
};

// Row-per-invoice-line companion to the trend chart, scoped to the same filters as the chart.
// The clicked bar segment only pre-sets removable grid filters (category + outcome); it never
// narrows the underlying query.
export const McdDispOutcomeDetailGrid = ({ rows, xAxis, category, bucket }) => {
  const gridRef = useRef();
  const [isGridReady, setIsGridReady] = useState(false);

  // ag-grid's onGridReady fires asynchronously; setDataSource before then throws
  useEffect(() => {
    if (!isGridReady) return;
    gridRef.current?.setDataSource(rows);

    const categoryField = xAxisToField[xAxis];
    const filterModel = {};
    if (categoryField && category) {
      filterModel[categoryField] = { filterType: 'text', type: 'equals', filter: category };
    }
    const bucketField = bucketToFilterField[bucket];
    if (bucketField) {
      filterModel[bucketField] = { filterType: 'number', type: 'notEqual', filter: 0 };
    }

    const gridApi = gridRef.current?.gridApiRef.current;
    gridApi?.setFilterModel(Object.keys(filterModel).length ? filterModel : null);
  }, [isGridReady, rows, xAxis, category, bucket]);

  return (
    <Grid
      ref={gridRef}
      gridOptions={{ fieldDefs, rowModelType: 'clientSide' }}
      events={{ onGridReady: () => setIsGridReady(true) }}
    />
  );
};
